/**
 * Shadow Store (Zustand)
 *
 * Manages shadow mode state for support/superadmin users.
 * Intentionally NOT persisted — shadow state must reset on refresh
 * for security (a support user accidentally leaving a shadow session
 * open won't persist across app restarts).
 *
 * No persistence, no DOM — same code on web and mobile.
 */
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

export interface ShadowTargetUser {
  id: string;
  email: string;
  name: string;
  profileImage?: string;
}

interface ShadowState {
  isActive: boolean;
  shadowToken: string | null;
  targetUser: ShadowTargetUser | null;
  expiresAt: number | null;
}

interface ShadowActions {
  startShadow: (
    token: string,
    targetUser: ShadowTargetUser,
    expiresIn: number
  ) => void;
  endShadow: () => void;
  isExpired: () => boolean;
}

export type ShadowStore = ShadowState & ShadowActions;

export const useShadowStore = create<ShadowStore>()(
  devtools(
    immer((set, get) => ({
      isActive: false,
      shadowToken: null,
      targetUser: null,
      expiresAt: null,

      startShadow: (token, targetUser, expiresIn) => {
        set(state => {
          state.isActive = true;
          state.shadowToken = token;
          state.targetUser = targetUser;
          state.expiresAt = Date.now() + expiresIn * 1000;
        });
      },

      endShadow: () => {
        set(state => {
          state.isActive = false;
          state.shadowToken = null;
          state.targetUser = null;
          state.expiresAt = null;
        });
      },

      isExpired: () => {
        const { expiresAt } = get();
        if (!expiresAt) return false;
        return Date.now() > expiresAt;
      },
    })),
    { name: 'shadow-store' }
  )
);

export const useShadowMode = () => useShadowStore(state => state.isActive);
export const useShadowTarget = () => useShadowStore(state => state.targetUser);
export const useShadowToken = () => useShadowStore(state => state.shadowToken);
